import { useRef, useState, useEffect } from "react"
import { motion, AnimatePresence } from "framer-motion"
import {
  Plus,
  Search,
  Dumbbell,
  ChevronDown,
  ChevronUp,
  Trash2,
  Pencil,
  Save,
  X,
  BookTemplate,
  Upload,
  Download,
  FileSpreadsheet,
  CheckCircle,
  AlertCircle,
  Users,
} from "lucide-react"
import {
  getTemplates,
  createRoutine,
  updateTemplateAndPropagate,
  deleteRoutine,
  type PropagationResult,
} from "../../api/routines"
import { getExercises } from "../../api/exercises"
import { parseRoutinesFromExcel, downloadRoutineTemplate, type ParsedRoutine } from "../../utils/parseExcel"
import type { Routine, Exercise, RepsUnit, WeightUnit } from "../../types"

const DAY_OPTIONS = [
  { value: 1, label: "Lunes" },
  { value: 2, label: "Martes" },
  { value: 3, label: "Miércoles" },
  { value: 4, label: "Jueves" },
  { value: 5, label: "Viernes" },
  { value: 6, label: "Sábado" },
  { value: 0, label: "Domingo" },
]

function getDayLabel(day?: number | null): string {
  if (day === undefined || day === null) return "Sin día"
  return DAY_OPTIONS.find((d) => d.value === day)?.label ?? "Sin día"
}

const MUSCLE_COLORS: Record<string, string> = {
  legs: "bg-green-500/20 text-green-400 border-green-500/20",
  chest: "bg-red-500/20 text-red-400 border-red-500/20",
  back: "bg-blue-500/20 text-blue-400 border-blue-500/20",
  shoulders: "bg-purple-500/20 text-purple-400 border-purple-500/20",
  arms: "bg-orange-500/20 text-orange-400 border-orange-500/20",
  core: "bg-yellow-500/20 text-yellow-400 border-yellow-500/20",
}

function getMuscleColor(group: string) {
  return MUSCLE_COLORS[group?.toLowerCase()] ?? "bg-white/10 text-white/60 border-white/10"
}

const REPS_UNITS: { value: RepsUnit; label: string; short: string }[] = [
  { value: "reps",          label: "Reps",               short: "reps"     },
  { value: "reps_per_side", label: "Reps/lado",          short: "reps/lado"},
  { value: "rir",           label: "RIR",                short: "RIR"      },
  { value: "rpe",           label: "Car. esfuerzo (RPE)", short: "RPE"     },
]

const WEIGHT_UNITS: { value: WeightUnit; label: string }[] = [
  { value: "kg",         label: "Kg"  },
  { value: "lb",         label: "Lb"  },
  { value: "rm_percent", label: "%RM" },
  { value: "bw_percent", label: "%PC" },
  { value: "rir",        label: "RIR" },
]

interface PendingEx {
  id?: number
  exercise_id: number
  sets: number
  reps: number
  reps_unit: RepsUnit
  weight?: number
  weight_unit: WeightUnit
  _destroy?: boolean
}

function TemplateCard({
  routine,
  exercises,
  onDelete,
  onUpdate,
}: {
  routine: Routine
  exercises: Exercise[]
  onDelete: () => void
  onUpdate: (r: Routine) => void
}) {
  const [expanded, setExpanded] = useState(false)
  const [editing, setEditing] = useState(false)
  const [saving, setSaving] = useState(false)
  const [propagated, setPropagated] = useState<PropagationResult | null>(null)
  const [name, setName] = useState(routine.name)
  const [description, setDescription] = useState(routine.description ?? "")
  const [selectedDay, setSelectedDay] = useState<number | "">(routine.scheduled_day ?? "")
  const [pending, setPending] = useState<PendingEx[]>(
    routine.routine_exercises.map((re) => ({
      id: re.id,
      exercise_id: re.exercise.id,
      sets: re.sets,
      reps: re.reps,
      reps_unit: re.reps_unit ?? "reps",
      weight: re.weight,
      weight_unit: re.weight_unit ?? "kg",
    }))
  )
  const [addExId, setAddExId] = useState<number>(exercises[0]?.id ?? 0)
  const [addSets, setAddSets] = useState(3)
  const [addReps, setAddReps] = useState(10)
  const [addRepsUnit, setAddRepsUnit] = useState<RepsUnit>("reps")
  const [addWeight, setAddWeight] = useState("")
  const [addWeightUnit, setAddWeightUnit] = useState<WeightUnit>("kg")

  const visible = pending.filter((p) => !p._destroy)
  const exerciseMap = Object.fromEntries(exercises.map((e) => [e.id, e]))

  function discardEdits() {
    setName(routine.name)
    setDescription(routine.description ?? "")
    setSelectedDay(routine.scheduled_day ?? "")
    setPending(
      routine.routine_exercises.map((re) => ({
        id: re.id,
        exercise_id: re.exercise.id,
        sets: re.sets,
        reps: re.reps,
        reps_unit: re.reps_unit ?? "reps",
        weight: re.weight,
        weight_unit: re.weight_unit ?? "kg",
      }))
    )
    setEditing(false)
  }

  function removeExercise(pendingIdx: number) {
    setPending((prev) =>
      prev[pendingIdx].id
        ? prev.map((p, i) => (i === pendingIdx ? { ...p, _destroy: true } : p))
        : prev.filter((_, i) => i !== pendingIdx)
    )
  }

  function addExercise() {
    if (!addExId) return
    setPending((prev) => [
      ...prev,
      {
        exercise_id: addExId,
        sets: addSets,
        reps: addReps,
        reps_unit: addRepsUnit,
        weight: addWeight ? Number(addWeight) : undefined,
        weight_unit: addWeightUnit,
      },
    ])
  }

  async function handleSave() {
    const derived = routine.derived_count ?? 0

    // Si la plantilla está asignada, el coach tiene que saber exactamente qué se
    // toca y qué no antes de guardar — sobre todo que los pesos no se pisan.
    if (derived > 0) {
      const clients = routine.derived_clients_count ?? 0
      const ok = confirm(
        `Esta plantilla está asignada a ${derived} rutina${derived !== 1 ? "s" : ""} ` +
          `de ${clients} cliente${clients !== 1 ? "s" : ""}.\n\n` +
          "Se actualizarán el nombre, las series y reps, y los ejercicios que agregues o quites, " +
          "en las rutinas de hoy en adelante.\n\n" +
          "El peso de cada cliente NO se modifica.\n\n" +
          "¿Continuar?"
      )
      if (!ok) return
    }

    setSaving(true)
    try {
      const params = {
        name,
        description: description || null,
        scheduled_day: selectedDay !== "" ? selectedDay : null,
        routine_exercises_attributes: pending.map((p) => ({
          ...(p.id ? { id: p.id } : {}),
          exercise_id: p.exercise_id,
          sets: p.sets,
          reps: p.reps,
          reps_unit: p.reps_unit,
          weight: p.weight,
          weight_unit: p.weight_unit,
          ...(p._destroy ? { _destroy: "1" } : {}),
        })),
      }

      // Siempre se pide propagar: quién recibe el cambio lo decide el backend. Si se
      // dependiera de derived_count (que se cargó al abrir la pestaña), una rutina
      // asignada después de eso no recibiría nada.
      const res = await updateTemplateAndPropagate(routine.id, params)
      const updated = res.routine
      setPropagated(res.propagated)
      // El PATCH no devuelve los contadores de derivadas (solo los calcula el
      // índice de plantillas): se conservan para que el badge no desaparezca.
      onUpdate({
        ...updated,
        derived_count: routine.derived_count,
        derived_clients_count: routine.derived_clients_count,
      })
      setPending(
        updated.routine_exercises.map((re) => ({
          id: re.id,
          exercise_id: re.exercise.id,
          sets: re.sets,
          reps: re.reps,
          reps_unit: re.reps_unit ?? "reps",
          weight: re.weight,
          weight_unit: re.weight_unit ?? "kg",
        }))
      )
      setEditing(false)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="gradient-card border border-white/10 rounded-xl overflow-hidden hover:border-white/20 transition-colors">
      {/* Header */}
      <div className="flex items-center gap-3 px-5 py-4">
        <div className="p-2 rounded-lg bg-primary/10 shrink-0">
          <BookTemplate size={16} className="text-primary" />
        </div>
        <button onClick={() => setExpanded((v) => !v)} className="flex-1 text-left min-w-0">
          <p className="font-semibold text-white truncate">{routine.name}</p>
          <p className="text-xs text-muted">
            {getDayLabel(routine.scheduled_day)} · {visible.length} ejercicio{visible.length !== 1 ? "s" : ""}
            {routine.description ? ` · ${routine.description}` : ""}
          </p>
        </button>
        <div className="flex items-center gap-1 shrink-0">
          {/* Rutinas asignadas que recibirían una edición de esta plantilla */}
          {(routine.derived_count ?? 0) > 0 && (
            <span
              className="mr-2 hidden items-center gap-1 rounded-full border border-primary/30 bg-primary/10 px-2 py-0.5 text-xs text-primary sm:flex"
              title={`Editar esta plantilla actualiza ${routine.derived_count} rutina(s) asignada(s) de ${routine.derived_clients_count} cliente(s), de hoy en adelante`}
            >
              <Users size={11} />
              {routine.derived_count}
            </span>
          )}
          {/* Muscle group pills */}
          {!expanded && visible.length > 0 && (
            <div className="hidden sm:flex gap-1 mr-2">
              {[...new Set(visible.map((p) => exerciseMap[p.exercise_id]?.muscle_group).filter(Boolean))].map(
                (group) => (
                  <span key={group} className={`rounded-full border px-2 py-0.5 text-xs ${getMuscleColor(group!)}`}>
                    {group}
                  </span>
                )
              )}
            </div>
          )}
          <button
            onClick={() => { setExpanded(true); setEditing(true); setPropagated(null) }}
            className="p-1.5 rounded-lg text-muted hover:text-primary hover:bg-primary/10 transition"
            title="Editar"
          >
            <Pencil size={14} />
          </button>
          <button
            onClick={onDelete}
            className="p-1.5 rounded-lg text-muted hover:text-danger hover:bg-danger/10 transition"
            title="Eliminar"
          >
            <Trash2 size={14} />
          </button>
          <button
            onClick={() => setExpanded((v) => !v)}
            className="p-1.5 rounded-lg text-muted hover:text-white transition"
          >
            {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>
        </div>
      </div>

      {/* Body */}
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="border-t border-white/10 overflow-hidden"
          >
            <div className="px-5 py-4 space-y-4">
              {/* Resultado de la última propagación a los programas de los clientes */}
              {propagated && propagated.routines > 0 && (
                <div className="flex items-start gap-2 rounded-lg border border-primary/30 bg-primary/10 px-3 py-2 text-xs text-primary">
                  <CheckCircle size={14} className="mt-0.5 shrink-0" />
                  <span>
                    {propagated.routines} rutina{propagated.routines !== 1 ? "s" : ""} actualizada
                    {propagated.routines !== 1 ? "s" : ""} en {propagated.clients} cliente
                    {propagated.clients !== 1 ? "s" : ""}. Los pesos de cada cliente se mantuvieron.
                  </span>
                </div>
              )}

              {/* Edit fields */}
              {editing && (
                <div className="space-y-3">
                  <div className="flex gap-3">
                    <input
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Nombre de la plantilla"
                      className="flex-1 rounded-lg border border-white/10 bg-surface-elevated px-3 py-2 text-sm text-white focus:border-primary"
                    />
                    <select
                      value={selectedDay}
                      onChange={(e) => setSelectedDay(e.target.value === "" ? "" : Number(e.target.value))}
                      className="rounded-lg border border-white/10 bg-surface-elevated px-3 py-2 text-sm text-white focus:border-primary"
                    >
                      <option value="">Sin día</option>
                      {DAY_OPTIONS.map((d) => (
                        <option key={d.value} value={d.value}>{d.label}</option>
                      ))}
                    </select>
                  </div>
                  <input
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Descripción (opcional)"
                    className="w-full rounded-lg border border-white/10 bg-surface-elevated px-3 py-2 text-sm text-white focus:border-primary"
                  />
                </div>
              )}

              {/* Exercise list */}
              {visible.length === 0 ? (
                <p className="text-sm text-muted italic">Sin ejercicios aún.</p>
              ) : (
                <div className="space-y-2">
                  {visible.map((p, idx) => {
                    const realIdx = pending.indexOf(p)
                    const ex = exerciseMap[p.exercise_id]
                    return (
                      <div key={idx} className="flex items-center gap-3 rounded-lg bg-surface-elevated px-3 py-2.5">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm font-medium text-white">{ex?.name ?? "Desconocido"}</span>
                            {ex && (
                              <span className={`rounded-full border px-2 py-0.5 text-xs ${getMuscleColor(ex.muscle_group)}`}>
                                {ex.muscle_group}
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-muted mt-0.5">
                            {p.sets} series × {p.reps} {REPS_UNITS.find(u => u.value === p.reps_unit)?.short ?? "reps"}
                            {p.weight ? ` · ${p.weight} ${WEIGHT_UNITS.find(u => u.value === p.weight_unit)?.label ?? "kg"}` : ""}
                          </p>
                        </div>
                        {editing && (
                          <button onClick={() => removeExercise(realIdx)} className="text-muted hover:text-danger transition shrink-0">
                            <X size={14} />
                          </button>
                        )}
                      </div>
                    )
                  })}
                </div>
              )}

              {/* Add exercise */}
              {editing && (
                <div className="rounded-lg border border-white/10 bg-surface p-4 space-y-3">
                  <p className="text-xs font-semibold text-muted uppercase tracking-wider">Agregar Ejercicio</p>
                  <div className="space-y-2">
                    <div className="grid grid-cols-3 gap-2">
                      <select
                        value={addExId}
                        onChange={(e) => setAddExId(Number(e.target.value))}
                        className="col-span-2 rounded-lg border border-white/10 bg-surface-elevated px-3 py-2 text-sm text-white focus:border-primary"
                      >
                        {exercises.map((ex) => (
                          <option key={ex.id} value={ex.id}>{ex.name} ({ex.muscle_group})</option>
                        ))}
                      </select>
                      <input type="number" min={1} value={addSets} onChange={(e) => setAddSets(Number(e.target.value))} placeholder="Series" className="rounded-lg border border-white/10 bg-surface-elevated px-3 py-2 text-sm text-white focus:border-primary" />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <input type="number" min={1} value={addReps} onChange={(e) => setAddReps(Number(e.target.value))} placeholder="Reps" className="rounded-lg border border-white/10 bg-surface-elevated px-3 py-2 text-sm text-white focus:border-primary" />
                      <select value={addRepsUnit} onChange={(e) => setAddRepsUnit(e.target.value as RepsUnit)} className="rounded-lg border border-white/10 bg-surface-elevated px-3 py-2 text-sm text-white focus:border-primary">
                        {REPS_UNITS.map((u) => <option key={u.value} value={u.value}>{u.label}</option>)}
                      </select>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <input type="number" value={addWeight} onChange={(e) => setAddWeight(e.target.value)} placeholder="Peso (opt)" className="rounded-lg border border-white/10 bg-surface-elevated px-3 py-2 text-sm text-white focus:border-primary" />
                      <select value={addWeightUnit} onChange={(e) => setAddWeightUnit(e.target.value as WeightUnit)} className="rounded-lg border border-white/10 bg-surface-elevated px-3 py-2 text-sm text-white focus:border-primary">
                        {WEIGHT_UNITS.map((u) => <option key={u.value} value={u.value}>{u.label}</option>)}
                      </select>
                    </div>
                  </div>
                  <button
                    onClick={addExercise}
                    className="w-full flex items-center justify-center gap-1 rounded-lg bg-primary/10 border border-primary/30 px-3 py-2 text-sm text-primary hover:bg-primary/20 transition"
                  >
                    <Plus size={14} />
                    Agregar Ejercicio
                  </button>
                </div>
              )}

              {/* Actions */}
              {editing && (
                <div className="flex gap-2">
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className="flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-black hover:opacity-90 transition glow-cyan disabled:opacity-50"
                  >
                    <Save size={14} />
                    {saving ? "Guardando..." : "Guardar Cambios"}
                  </button>
                  <button
                    onClick={discardEdits}
                    className="flex items-center gap-2 rounded-lg border border-white/10 px-4 py-2 text-sm text-muted hover:text-white transition"
                  >
                    <X size={14} />
                    Cancelar
                  </button>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

// ── Main Component ─────────────────────────────────────────────────────────
export default function RoutineGalleryTab() {
  const [templates, setTemplates] = useState<Routine[]>([])
  const [exercises, setExercises] = useState<Exercise[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState("")

  // Excel import state
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [showImport, setShowImport] = useState(false)
  const [parsedRoutines, setParsedRoutines] = useState<ParsedRoutine[]>([])
  const [importing, setImporting] = useState(false)
  const [importDone, setImportDone] = useState<{ ok: number; fail: number } | null>(null)

  // New template form
  const [showNew, setShowNew] = useState(false)
  const [newName, setNewName] = useState("")
  const [newDesc, setNewDesc] = useState("")
  const [newDay, setNewDay] = useState<number | "">("")
  const [creating, setCreating] = useState(false)

  useEffect(() => {
    Promise.all([getTemplates(), getExercises()])
      .then(([tpls, exs]) => {
        setTemplates(tpls)
        setExercises(exs)
      })
      .finally(() => setLoading(false))
  }, [])

  async function handleCreate() {
    if (!newName.trim()) return
    setCreating(true)
    try {
      const routine = await createRoutine({
        name: newName,
        description: newDesc || undefined,
        scheduled_day: newDay !== "" ? newDay : undefined,
        is_template: true,
      })
      setTemplates((prev) => [routine, ...prev])
      setNewName("")
      setNewDesc("")
      setNewDay("")
      setShowNew(false)
    } finally {
      setCreating(false)
    }
  }

  async function handleFileSelect(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return
    const rows = await parseRoutinesFromExcel(file)
    setParsedRoutines(rows)
    setImportDone(null)
    setShowImport(true)
    e.target.value = ""
  }

  async function handleImport() {
    setImporting(true)
    const exerciseByName = Object.fromEntries(
      exercises.map((e) => [e.name.toLowerCase().trim(), e])
    )
    let ok = 0, fail = 0

    for (const pr of parsedRoutines) {
      const resolvedExercises = pr.exercises
        .map((pe) => {
          const ex = exerciseByName[pe.exercise_name.toLowerCase().trim()]
          if (!ex) return null
          return {
            exercise_id: ex.id,
            sets: pe.sets,
            reps: pe.reps,
            weight: pe.weight,
            tempo: pe.tempo,
            notes: pe.notes,
          }
        })
        .filter(Boolean)

      try {
        const created = await createRoutine({
          name: pr.name,
          description: pr.description,
          scheduled_day: pr.scheduled_day,
          is_template: true,
          routine_exercises_attributes: resolvedExercises,
        })
        setTemplates((prev) => [created, ...prev])
        ok++
      } catch {
        fail++
      }
    }

    setImporting(false)
    setImportDone({ ok, fail })
  }

  async function handleDelete(id: number) {
    if (!confirm("¿Eliminar esta plantilla y todos sus ejercicios?")) return
    await deleteRoutine(id)
    setTemplates((prev) => prev.filter((r) => r.id !== id))
  }

  const filtered = templates.filter(
    (t) => !search || t.name.toLowerCase().includes(search.toLowerCase())
  )

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Dumbbell size={40} className="text-primary animate-pulse-glow" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center gap-3">
        <div>
          <h2
            className="text-sm font-semibold text-white uppercase tracking-widest"
            style={{ fontFamily: "var(--font-display)" }}
          >
            Galería de Rutinas
          </h2>
          <p className="text-xs text-muted">
            Plantillas reutilizables · {templates.length} plantilla{templates.length !== 1 ? "s" : ""}
          </p>
        </div>
        <div className="ml-auto flex flex-wrap gap-2">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar plantilla..."
              className="rounded-lg border border-white/10 bg-surface-elevated pl-9 pr-3 py-2 text-sm text-white focus:border-primary w-48"
            />
          </div>
          <button
            onClick={() => fileInputRef.current?.click()}
            className="flex items-center gap-2 rounded-lg border border-white/20 px-4 py-2 text-sm font-medium text-muted hover:text-white hover:border-white/40 transition"
          >
            <Upload size={15} />
            Importar Excel
          </button>
          <input ref={fileInputRef} type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={handleFileSelect} />
          <button
            onClick={() => setShowNew(true)}
            className="flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-black hover:opacity-90 transition glow-cyan"
          >
            <Plus size={16} />
            Nueva Plantilla
          </button>
        </div>
      </div>

      {/* Excel Import Modal */}
      <AnimatePresence>
        {showImport && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
            onClick={() => !importing && setShowImport(false)}
          >
            <motion.div
              initial={{ scale: 0.92, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.92, opacity: 0 }}
              className="gradient-card border border-white/20 rounded-2xl p-6 max-w-2xl w-full space-y-4 max-h-[85vh] flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex items-center justify-between shrink-0">
                <div className="flex items-center gap-2">
                  <FileSpreadsheet size={18} className="text-primary" />
                  <h3 className="text-lg font-semibold text-white">Importar Rutinas</h3>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={downloadRoutineTemplate}
                    className="flex items-center gap-1.5 rounded-lg border border-white/10 px-3 py-1.5 text-xs text-muted hover:text-white transition"
                  >
                    <Download size={12} />
                    Descargar Plantilla
                  </button>
                  <button onClick={() => setShowImport(false)} className="p-2 rounded-lg text-muted hover:text-white hover:bg-white/10 transition">
                    <X size={18} />
                  </button>
                </div>
              </div>

              <p className="text-xs text-muted/70 shrink-0">
                Los ejercicios se buscan por nombre exacto en tu Biblioteca. Asegurate de crearlos primero.
              </p>

              {/* Preview */}
              <div className="overflow-auto flex-1 space-y-3">
                {parsedRoutines.map((pr, i) => {
                  const exerciseByName = Object.fromEntries(exercises.map((e) => [e.name.toLowerCase().trim(), e]))
                  const notFound = pr.exercises.filter((pe) => !exerciseByName[pe.exercise_name.toLowerCase().trim()])
                  return (
                    <div key={i} className="rounded-lg border border-white/10 bg-surface-elevated p-3 space-y-2">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-semibold text-white">{pr.name}</p>
                          <p className="text-xs text-muted">
                            {pr.exercises.length} ejercicio{pr.exercises.length !== 1 ? "s" : ""}
                            {pr.scheduled_day !== undefined ? ` · ${["Dom","Lun","Mar","Mié","Jue","Vie","Sáb"][pr.scheduled_day]}` : ""}
                          </p>
                        </div>
                        {notFound.length > 0 ? (
                          <span className="flex items-center gap-1 text-xs text-amber-400">
                            <AlertCircle size={12} />
                            {notFound.length} no encontrado{notFound.length !== 1 ? "s" : ""}
                          </span>
                        ) : (
                          <span className="flex items-center gap-1 text-xs text-green-400">
                            <CheckCircle size={12} />
                            Listo
                          </span>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {pr.exercises.map((pe, j) => {
                          const found = !!exerciseByName[pe.exercise_name.toLowerCase().trim()]
                          return (
                            <span key={j} className={`rounded px-2 py-0.5 text-xs border ${found ? "border-white/10 text-white/70" : "border-amber-500/30 text-amber-400"}`}>
                              {pe.exercise_name} {pe.sets}×{pe.reps}
                            </span>
                          )
                        })}
                      </div>
                    </div>
                  )
                })}
              </div>

              {importDone && (
                <p className="text-sm text-center shrink-0">
                  <span className="text-green-400 font-semibold">{importDone.ok} rutinas importadas</span>
                  {importDone.fail > 0 && <span className="text-red-400"> · {importDone.fail} fallaron</span>}
                </p>
              )}

              {/* Actions */}
              <div className="flex gap-2 shrink-0">
                {!importDone ? (
                  <button
                    onClick={handleImport}
                    disabled={importing || parsedRoutines.length === 0}
                    className="flex-1 flex items-center justify-center gap-2 rounded-lg bg-primary py-2.5 text-sm font-semibold text-black hover:opacity-90 transition disabled:opacity-50"
                  >
                    <Upload size={14} />
                    {importing ? "Importando..." : `Importar ${parsedRoutines.length} rutina${parsedRoutines.length !== 1 ? "s" : ""}`}
                  </button>
                ) : (
                  <button
                    onClick={() => setShowImport(false)}
                    className="flex-1 flex items-center justify-center gap-2 rounded-lg bg-primary py-2.5 text-sm font-semibold text-black hover:opacity-90 transition"
                  >
                    Listo
                  </button>
                )}
                <button
                  onClick={() => setShowImport(false)}
                  disabled={importing}
                  className="flex items-center gap-2 rounded-lg border border-white/10 px-5 py-2.5 text-sm text-muted hover:text-white transition"
                >
                  Cancelar
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* New Template Form */}
      <AnimatePresence>
        {showNew && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="gradient-card border border-primary/30 rounded-xl p-5 space-y-3"
          >
            <h3 className="text-sm font-semibold text-white">Nueva Plantilla</h3>
            <div className="flex gap-3">
              <input
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="Ej: Día de Piernas"
                className="flex-1 rounded-lg border border-white/10 bg-surface-elevated px-3 py-2.5 text-sm text-white focus:border-primary"
              />
              <select
                value={newDay}
                onChange={(e) => setNewDay(e.target.value === "" ? "" : Number(e.target.value))}
                className="rounded-lg border border-white/10 bg-surface-elevated px-3 py-2.5 text-sm text-white focus:border-primary"
              >
                <option value="">Sin día</option>
                {DAY_OPTIONS.map((d) => (
                  <option key={d.value} value={d.value}>{d.label}</option>
                ))}
              </select>
            </div>
            <input
              value={newDesc}
              onChange={(e) => setNewDesc(e.target.value)}
              placeholder="Descripción (opcional)"
              className="w-full rounded-lg border border-white/10 bg-surface-elevated px-3 py-2.5 text-sm text-white focus:border-primary"
            />
            <div className="flex gap-2">
              <button
                onClick={handleCreate}
                disabled={creating}
                className="flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-black hover:opacity-90 transition disabled:opacity-50"
              >
                <Save size={14} />
                {creating ? "Creando..." : "Crear Plantilla"}
              </button>
              <button
                onClick={() => { setNewName(""); setNewDesc(""); setNewDay(""); setShowNew(false) }}
                className="flex items-center gap-2 rounded-lg border border-white/10 px-4 py-2 text-sm text-muted hover:text-white transition"
              >
                <X size={14} />
                Cancelar
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Template List */}
      {filtered.length === 0 && !showNew ? (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="gradient-card border border-white/10 rounded-xl p-12 text-center"
        >
          <BookTemplate size={48} className="mx-auto mb-4 text-muted opacity-30" />
          <p className="text-muted text-sm">
            {search ? "No hay plantillas con ese nombre." : "No hay plantillas aún."}
          </p>
          <p className="mt-1 text-xs text-muted/60">
            Crea plantillas aquí y luego impórtalas desde el programa de cada cliente.
          </p>
        </motion.div>
      ) : (
        <div className="space-y-3">
          {filtered.map((t) => (
            <TemplateCard
              key={t.id}
              routine={t}
              exercises={exercises}
              onDelete={() => handleDelete(t.id)}
              onUpdate={(updated) =>
                setTemplates((prev) => prev.map((x) => (x.id === updated.id ? updated : x)))
              }
            />
          ))}
        </div>
      )}
    </div>
  )
}
